/* =========================================================================
   VroMotion — the few moves every quiz on the theme makes, done one way.

   Add-on only. Nothing here runs on its own; the quiz scripts
   (vro-subfilter.js, vro-plp.js, the quiz sections) call it when it is
   there and fall back to what they did before when it is not.

   Why it exists: the "harsh blink" was never one bug. It was three habits
   repeated in every quiz —
     1. new product cards were put on screen before their photos had
        loaded, so a row went white and then filled in picture by picture;
     2. a box whose content changed (the Noted bar growing a second line,
        a row going from five cards to three) jumped to its new height in
        one frame;
     3. text was replaced by rebuilding the element, so the tick re-popped
        and the line flashed.
   Each helper below removes one of those.
   ========================================================================= */
(function () {
  'use strict';
  if (window.VroMotion) return;

  var EASE = 'cubic-bezier(.22, .9, .3, 1)';

  function reduced() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* ---------------------------------------------------------------
     preload(cards, { limit, timeout })

     Resolves once the first photos of the given cards are decoded (or the
     timeout passes — a slow network never holds the answer hostage). The
     cards may be detached from the page: a lazy <img> outside the
     document never loads, so a probe Image with the same srcset/sizes is
     what fetches it, and the card's own <img> is switched to eager so it
     paints from cache the moment it is inserted.
  --------------------------------------------------------------- */
  function preload(cards, opts) {
    opts = opts || {};
    var limit = opts.limit || 8;
    var timeout = opts.timeout || 1400;
    var list = Array.prototype.slice.call(cards || [], 0, limit);
    var jobs = [];

    list.forEach(function (card) {
      if (!card || !card.querySelectorAll) return;
      var img = card.querySelector('img.feed-card-img:not(.feed-card-img--hover)') ||
                card.querySelector('img');
      if (!img) return;
      img.loading = 'eager';
      if (img.isConnected && img.complete && img.naturalWidth) return;
      var probe = new Image();
      probe.decoding = 'async';
      if (img.sizes) probe.sizes = img.sizes;
      if (img.srcset) probe.srcset = img.srcset;
      probe.src = img.currentSrc || img.src;
      jobs.push(new Promise(function (resolve) {
        var finish = function () { resolve(); };
        if (probe.decode) {
          probe.decode().then(finish, finish);
        } else {
          probe.onload = finish;
          probe.onerror = finish;
        }
      }));
    });

    if (!jobs.length) return Promise.resolve();
    return Promise.race([
      Promise.all(jobs),
      new Promise(function (resolve) { setTimeout(resolve, timeout); })
    ]);
  }

  /* ---------------------------------------------------------------
     morphHeight(el, mutate, { duration })

     Runs mutate(), and if that changed el's height, animates from the
     old height to the new one instead of jumping. Safe to call again
     while a previous morph is still running: it starts from wherever the
     box is at that moment.
  --------------------------------------------------------------- */
  function morphHeight(el, mutate, opts) {
    opts = opts || {};
    if (!el || reduced() || el.offsetParent === null) {
      if (mutate) mutate();
      return;
    }
    var duration = opts.duration || 420;
    var from = el.getBoundingClientRect().height;

    if (el.__vroMorphEnd) el.__vroMorphEnd(null, true);

    if (mutate) mutate();
    /* A rail that has just appeared draws its dots a frame later, which
       landed after the height below was measured: the box eased to a
       height a row of dots short, stopped, then snapped open. Drawn now,
       so the height measured is the height it ends at. */
    if (window.VroRailDots) {
      try { window.VroRailDots.init(el); window.VroRailDots.refresh(); } catch (err) { /* dots are optional */ }
    }

    var prevTransition = el.style.transition;
    /* Whatever the stylesheet already transitions (a rail's opacity dip)
       keeps running alongside the height. */
    var base = prevTransition;
    if (!base) {
      try { base = window.getComputedStyle(el).transition || ''; } catch (err) { base = ''; }
      if (/^(all 0s|none)/.test(base)) base = '';
    }
    var heightOnly = 'height ' + duration + 'ms ' + (opts.ease || EASE);
    el.style.height = '';
    var to = el.getBoundingClientRect().height;
    if (Math.abs(to - from) < 1) return;

    /* clip: hides the overflow during the move without making a
       horizontal rail forget where it was scrolled. */
    el.style.overflowY = 'clip';
    el.style.height = from + 'px';
    void el.offsetHeight;
    el.style.transition = base ? base + ', ' + heightOnly : heightOnly;
    el.style.height = to + 'px';

    var timer;
    var end = function (e, interrupted) {
      if (e && (e.target !== el || e.propertyName !== 'height')) return;
      clearTimeout(timer);
      el.removeEventListener('transitionend', end);
      el.__vroMorphEnd = null;
      el.style.transition = prevTransition;
      el.style.height = '';
      el.style.overflowY = '';
      /* Something inside grew (or shrank) while the box was moving — a
         late photo, a line wrapping. Rather than snapping the difference
         at the end, ease the rest of the way from where it stopped. Once:
         a box that keeps changing is left to size itself. */
      if (!opts.followUp && !interrupted) {
        var now = el.getBoundingClientRect().height;
        if (Math.abs(now - to) > 1) {
          el.style.height = to + 'px';
          morphHeight(el, function () { el.style.height = ''; }, {
            duration: 220, ease: opts.ease, followUp: true
          });
        }
      }
    };
    el.__vroMorphEnd = end;
    el.addEventListener('transitionend', end);
    timer = setTimeout(end, duration + 80);
  }

  /* ---------------------------------------------------------------
     fade / swapText(el, text)

     Changes a line's words with a short fade instead of a flash. Does
     nothing when the words are already right, so calling it on every
     update is free. wrap(apply), when given, is handed the actual text
     change — so a caller can morph the box around it.
  --------------------------------------------------------------- */
  function fade(el, from, to, ms, fill) {
    if (!el.animate) {
      el.style.opacity = to >= 1 ? '' : String(to);
      return { finished: Promise.resolve(), cancel: function () {} };
    }
    return el.animate([{ opacity: from }, { opacity: to }],
      { duration: ms, easing: 'ease', fill: fill ? 'forwards' : 'none' });
  }

  function swapText(el, text, wrap) {
    if (!el) return;
    if (el.__vroSwapTo === text) return;
    if (el.__vroSwapTo === undefined && el.textContent === text) return;
    el.__vroSwapTo = text;
    var apply = function () { el.textContent = el.__vroSwapTo; };
    var put = function () { if (wrap) wrap(apply); else apply(); };
    if (reduced() || !el.isConnected || !el.animate) { put(); return; }
    if (el.__vroSwapAnim) el.__vroSwapAnim.cancel();
    var out = fade(el, 1, 0, 150, true);
    el.__vroSwapAnim = out;
    out.finished.then(function () {
      if (el.__vroSwapAnim !== out) return;
      put();
      var back = fade(el, 0, 1, 220, false);
      out.cancel();
      el.__vroSwapAnim = back;
    }, function () { /* cancelled by a newer swap */ });
  }

  /* ---------------------------------------------------------------
     crossfade(container, swap)

     Dips a container's current content (never to blank), runs swap()
     — which puts the new content in — and brings it back up. The
     container's height is morphed across the swap so nothing below it
     jumps. Opacity is driven by the Web Animations API so it never
     fights the height transition.
  --------------------------------------------------------------- */
  function crossfade(container, swap, opts) {
    opts = opts || {};
    if (!container || reduced() || !container.animate) { if (swap) swap(); return Promise.resolve(); }
    var dip = opts.dip != null ? opts.dip : 0.4;
    var out = fade(container, 1, dip, opts.out || 170, true);
    return out.finished.then(function () {
      morphHeight(container, swap, { duration: opts.duration || 440 });
      var back = fade(container, dip, 1, opts.in || 300, false);
      out.cancel();
      return back.finished;
    }).catch(function () { /* interrupted */ });
  }

  /* ---------------------------------------------------------------
     pop(els)

     How a quiz's answer arrives, on every quiz in the theme: each card
     scales up from .82 with a slight overshoot (the shuffle's Pop), 70ms
     after the one before it, the run capped at 400ms. Class-driven
     (.vro-pop-in, assets/vro-personalization.css) so reduced motion is
     settled in the stylesheet. The class comes off at the end so the
     same cards can pop again on the next answer.
  --------------------------------------------------------------- */
  var POP_STEP = 70;
  var POP_MAX = 400;
  function popDelay(index) { return Math.min(index * POP_STEP, POP_MAX); }
  function pop(els) {
    if (!els) return;
    Array.prototype.forEach.call(els, function (node, i) {
      if (!node || !node.classList) return;
      node.classList.remove('vro-pop-in');
      node.style.animationDelay = popDelay(i) + 'ms';
      void node.offsetWidth;
      node.classList.add('vro-pop-in');
      var done = function (e) {
        if (e && e.target !== node) return;
        node.classList.remove('vro-pop-in');
        node.style.animationDelay = '';
        node.removeEventListener('animationend', done);
      };
      node.addEventListener('animationend', done);
    });
  }

  /* ---------------------------------------------------------------
     settle(el, mutate) / hold(el, from) / release(el)

     A quiz card answering, the way sort-a-concern's card does it: the
     question is swapped for the answer in one frame and only the card's
     height moves, .45s on the standard ease. While the answer's products
     are still loading the card is held at the height it had (hold), so it
     does not shrink to a "Finding…" line and then jump open; release
     eases it once to the height the products need.
  --------------------------------------------------------------- */
  var SETTLE = { duration: 450, ease: 'cubic-bezier(.4, 0, .2, 1)' };
  function settle(el, mutate) { morphHeight(el, mutate, SETTLE); }
  function hold(el, from) {
    if (!el || !from || reduced()) return;
    if (el.__vroMorphEnd) el.__vroMorphEnd(null, true);
    el.__vroHeld = true;
    el.style.height = from + 'px';
    el.style.overflowY = 'clip';
  }
  function release(el) {
    if (!el || !el.__vroHeld) return;
    el.__vroHeld = false;
    morphHeight(el, function () {
      el.style.height = '';
      el.style.overflowY = '';
    }, SETTLE);
  }

  window.VroMotion = {
    settle: settle,
    hold: hold,
    release: release,
    pop: pop,
    popDelay: popDelay,
    preload: preload,
    morphHeight: morphHeight,
    swapText: swapText,
    crossfade: crossfade,
    fade: fade,
    reduced: reduced
  };
})();
